export type ConsentStatus = 'accepted' | 'rejected'

export type CookiePreferences = {
  status: ConsentStatus
  functional: boolean
}

type StoredConsent = CookiePreferences & {
  version: number
  consentedAt: string
}

const CONSENT_VERSION = 3
const CONSENT_KEY = 'archestor-consent'

const CRAWLER_PATTERN = /googlebot|google-inspectiontool|googleother|google-extended|apis-google|feedfetcher-google|storebot-google|bingbot|applebot|yandex|duckduckbot|baiduspider|slurp|facebookexternalhit|twitterbot|linkedinbot|semrushbot|ahrefsbot|mj12bot|dotbot/i

function getUserAgent(): string | undefined {
  if (import.meta.server) {
    return useRequestHeaders(['user-agent'])['user-agent']
  }

  if (import.meta.client) {
    return navigator.userAgent
  }

  return undefined
}

function isCrawlerVisit(): boolean {
  const userAgent = getUserAgent()
  if (!userAgent) return false
  return CRAWLER_PATTERN.test(userAgent)
}

const consentCookieOptions = {
  maxAge: 60 * 60 * 24 * 365,
  path: '/',
  sameSite: 'lax' as const
}

type ConsentUiStatus = 'unset' | ConsentStatus

function parseStoredConsent(raw: string | null | undefined): StoredConsent | null {
  if (!raw) return null

  try {
    const parsed = JSON.parse(raw) as StoredConsent
    if (parsed.version !== CONSENT_VERSION) return null
    return parsed
  } catch {
    return null
  }
}

export function useCookieConsent() {
  const consentCookie = useCookie<string | null>(CONSENT_KEY, consentCookieOptions)
  const consentStatus = useState<ConsentUiStatus>('cookie-consent-status', () => 'unset')
  const showBanner = useState('cookie-consent-banner', () => false)
  const isCrawler = useState('cookie-consent-crawler', () => false)
  const { teardownTurnstile } = useTurnstile()

  const hasAccepted = computed(() => consentStatus.value === 'accepted')

  const readStoredConsent = (): StoredConsent | null => parseStoredConsent(consentCookie.value)

  const writeConsent = (preferences: CookiePreferences) => {
    const payload: StoredConsent = {
      version: CONSENT_VERSION,
      status: preferences.status,
      functional: preferences.functional,
      consentedAt: new Date().toISOString()
    }

    consentCookie.value = JSON.stringify(payload)
  }

  const clearAllCookies = () => {
    consentCookie.value = null

    const localeCookie = useCookie<string | null>('archestor-locale', { path: '/' })
    const countryCookie = useCookie<string | null>('archestor-country', { path: '/' })
    localeCookie.value = null
    countryCookie.value = null

    if (import.meta.client) {
      try {
        localStorage.removeItem('archestor-locale')
        localStorage.removeItem('archestor-a11y')
      } catch {}
    }
  }

  const initConsent = () => {
    if (isCrawlerVisit()) {
      isCrawler.value = true
      consentStatus.value = 'accepted'
      showBanner.value = false
      return
    }

    isCrawler.value = false

    const stored = readStoredConsent()

    if (!stored) {
      consentStatus.value = 'unset'
      showBanner.value = true
      return
    }

    if (stored.status !== 'accepted') {
      clearAllCookies()
      consentStatus.value = 'unset'
      showBanner.value = true
      return
    }

    consentStatus.value = 'accepted'
    showBanner.value = false

    if (import.meta.client) {
      loadTurnstileWithConsent()
    }
  }

  const acceptAll = () => {
    consentStatus.value = 'accepted'
    showBanner.value = false
    writeConsent({ status: 'accepted', functional: true })
    saveLocaleAfterConsent()

    if (import.meta.client) {
      loadTurnstileWithConsent()
    }
  }

  const rejectAll = () => {
    teardownTurnstile()
    clearAllCookies()
    consentStatus.value = 'unset'
    showBanner.value = false
  }

  const openBanner = () => {
    if (isCrawlerVisit()) return
    showBanner.value = true
  }

  return {
    hasAccepted,
    showBanner,
    isCrawler,
    initConsent,
    acceptAll,
    rejectAll,
    openBanner
  }
}

function readConsentCookie(): StoredConsent | null {
  const cookie = useCookie<string | null>(CONSENT_KEY, consentCookieOptions)
  return parseStoredConsent(cookie.value)
}

export function hasAcceptedConsent(): boolean {
  const stored = readConsentCookie()
  return stored?.status === 'accepted'
}

export function hasRejectedConsent(): boolean {
  return false
}

export function hasFunctionalConsent(): boolean {
  const stored = readConsentCookie()
  return stored?.status === 'accepted' && stored.functional === true
}